<template>
    <div ref="screenContainer" class="screenContainer" id="screenContainer">
        <div class="iconRow">
            <div @click="toggleFullscreen" class="nwIcon" text="Fullscreen">
                <i class="fa-solid fa-maximize"></i>
            </div>
            <div @click="refreshGraph" class="refreshIcon" text="Refresh">
                <i class="fa-solid fa-refresh"></i>
            </div>
        </div>
        <div v-if="debug.enabled" class="debug-bar">
            <div class="debug-header" @click="debug.expanded = !debug.expanded">Rule Debug (click to {{ debug.expanded ? 'collapse' : 'expand' }})</div>
            <div v-if="debug.expanded" class="debug-content">
                <div>Level1 nodes: {{ debug.stats.level1Nodes }}</div>
                <div>Level2 nodes: {{ debug.stats.level2Nodes }}</div>
                <div>Total edges (final): {{ debug.stats.finalEdges }}</div>
                <div>Edge types available: {{ filteredEdgeTypes.length }}</div>
            </div>
        </div>
        <!-- Wrapper to avoid Vue mutating inside the active Sigma container -->
        <div class="sigma-wrapper">
            <div v-if="loading" class="sigma-empty">Loading...</div>
            <div v-else-if="errorMessage" class="sigma-empty">{{ errorMessage }}</div>
            <div v-else-if="!hasGraphData" class="sigma-empty">No connections to show</div>
            <div v-else ref="sigmaContainer" class="sigma-container"></div>
        </div>
        <div class="legend-box">
            <div class="legend">
                <h3>Legend for nodes</h3>
                <div class="legend-grid" :class="{ 'legend-grid-extended': groupedLegendItems.length > 6 }">
                    <!-- Grouped legend items -->
                    <div v-for="(group, index) in groupedLegendItems" :key="index" class="legend-group">
                        <div class="legend-group-header">
                            <span class="legend-color" :style="{ 'background-color': group.primaryColor }"></span>
                            <span class="legend-group-title">{{ group.baseTypeName }}</span>
                        </div>
                        <div class="legend-levels">
                            <div v-for="levelInfo in group.levels" :key="levelInfo.level" class="legend-level-item">
                                <span class="legend-level-indicator" :style="{ 'background-color': levelInfo.color }"></span>
                                <span class="legend-level-text">
                                    {{
                                    levelInfo.level === 0 ? 'Center' : (levelInfo.level === 1 ? '1st level' : '2nd level')
                                    }} ({{ levelInfo.nodeCount }})
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="legend">
                <h3>Legends for lines/ connections</h3>
                <div class="edge-legend-grid" v-if="edgeConfig">
                    <!-- Level 1 connections -->
                    <div class="edge-legend-item" style="grid-column: 1 / -1; font-weight: bold;">Level 1 connections</div>
                    <div v-for="edgeType in filteredEdgeTypesL1" :key="'l1-' + edgeType.TltId" class="edge-legend-item">
                        <div class="edge-legend-header">
                            <input type="checkbox" :id="'filter-l1-' + edgeType.TltId" :value="edgeType.TltId" v-model="selectedEdgeTypesL1" @change="onFilterChange" checked />
                            <label :for="'filter-l1-' + edgeType.TltId">{{ edgeType.TliName }}</label>
                        </div>
                        <ul class="edge-classification-list">
                            <li v-for="classification in edgeType.classifications" :key="'l1-' + edgeType.TltId + '-' + classification.value" class="edge-classification-item">
                                <span class="edge-classification-line" :style="{ 'border-bottom': classification.width + 'px solid ' + classification.color }"></span>{{ classification.name }}
                            </li>
                        </ul>
                    </div>

                    <!-- Level 2 connections (per first-level type) -->
                    <template v-if="showSecondLevel">
                        <div class="edge-legend-item" style="grid-column: 1 / -1; font-weight: bold;">Level 2 connections</div>
                        <template v-for="section in l2TypeHeaders" :key="'l2sec-' + section.ttyId">
                            <div class="edge-legend-item" style="grid-column: 1 / -1; text-decoration: underline;">From {{ section.ttyName }}</div>
                            <div v-for="edgeType in (filteredEdgeTypesL2ByType[section.ttyId] || [])" :key="'l2-' + section.ttyId + '-' + edgeType.TltId" class="edge-legend-item">
                                <div class="edge-legend-header">
                     <input type="checkbox" :id="'filter-l2-' + section.ttyId + '-' + edgeType.TltId"
                                           :value="edgeType.TltId"
                                           v-model="selectedEdgeTypesL2ByType[section.ttyId]"
                                           @change="onFilterChange" checked />
                     <label :for="'filter-l2-' + section.ttyId + '-' + edgeType.TltId">{{ edgeType.TliName }}</label>
                                </div>
                                <ul class="edge-classification-list">
                                    <li v-for="classification in edgeType.classifications" :key="'l2-' + section.ttyId + '-' + edgeType.TltId + '-' + classification.value" class="edge-classification-item">
                                        <span class="edge-classification-line" :style="{ 'border-bottom': classification.width + 'px solid ' + classification.color }"></span>{{ classification.name }}
                                    </li>
                                </ul>
                            </div>
                        </template>
                    </template>
                </div>
            </div>

            <div class="level-toggle">
                <input type="checkbox" id="toggle-second-level" v-model="showSecondLevel" @change="onLevelToggle" />
                <label for="toggle-second-level">2nd level</label>
            </div>
            <!-- Provider/Prime toggle -->
            <div class="edge-legend-item" v-if="providerPrimeApplicable">
                <div class="edge-legend-header">
                    <input type="checkbox" id="filter-project-stakeholder-provider-prime" v-model="filterProjectStakeholderProviderPrime" @change="onFilterChange" />
                    <label for="filter-project-stakeholder-provider-prime">Project-Provider,Prime</label>
                </div>
            </div>
            <!-- Stakeholder Role Filter -->
            <div class="stakeholder-role-filter" v-if="stakeholderRoleFilterApplicable && availableStakeholderRoles.length > 0">
                <div class="stakeholder-role-header">{{ stakeholderRoleFilterLabel }}</div>
                <div class="stakeholder-role-options">
                    <div v-for="role in availableStakeholderRoles" :key="'role-' + role.value" class="stakeholder-role-item">
                        <input type="checkbox" 
                               :id="'filter-stakeholder-role-' + role.value" 
                               :value="role.value" 
                               v-model="selectedStakeholderRoles" 
                               @change="onFilterChange" />
                        <label :for="'filter-stakeholder-role-' + role.value">{{ role.label }}</label>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="popupVisible" class="popover">
            <div class="popover-content">
                <div class="prevHeaderBar">
                    <div class="prevTypeName">{{ selectedNode.TtyName }}</div>
                    <button @click="popupVisible = false" class="close-button"><i class="fa-solid fa-times"></i></button>
                </div>
                <div v-if="selectedNode.ImgUrl > ''">
                    <img class="imgPreview" :src="selectedNode.ImgUrl" alt="No Image" />
                </div>
                <div class="prevBody">
                    <h3>{{ selectedNode.Title }}</h3>
                    <p class="prevDescription">{{ selectedNode.Description }}</p>
                </div>
                <div class="prevButtons">
                    <DxButton @click="onMoreInfoClick" text="More Info"></DxButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { ref, onMounted, onBeforeUnmount, computed, nextTick, watch } from "vue";
    import { DxButton } from "devextreme-vue/button";
    import Sigma from "sigma";
    import { EdgeArrowProgram, EdgeDoubleArrowProgram } from "sigma/rendering";
    import Graph from "graphology";
    import axios from "axios";
    import { alert } from "devextreme/ui/dialog";

    /* ---------------------------------- Constants ---------------------------------- */
    const baseUri = "WebApi.asmx/api/chartService";
    const WIDTH = 1000;
    const HEIGHT = 1000;
    const EDGE_SIZE_DEFAULT = 2;
    const EDGE_COLOR_DEFAULT = "rgba(0,0,0,.7)";
    const ARROW_SIZE_MULTIPLIER = 1.8; // enlarge arrow head & shaft proportionally
    const NODE_RADIUS = 5;
    const CENTER_NODE_RADIUS = 7;
    const STAKEHOLDER_TTY = 16; // Previously inline magic number
    const PROJECT_STAKEHOLDER_EDGE_TLT = 4; // Edge type filtered when provider/prime unchecked
    const POLICY_STAKEHOLDER_EDGE_TLT = 10; // Policy-Stakeholder edge type for role filtering
    const BASE_RADIUS_LEVEL1 = Math.min(WIDTH, HEIGHT) / 8;
    const BASE_RADIUS_LEVEL2 = Math.min(WIDTH, HEIGHT) / 5;

    // Stakeholder role filtering for Policy-Stakeholder edges (TltId 10).
    // Stakeholder roles are derived from link attributes (LinkAttributes / LinkMultiSelect001),
    // rather than directly from Topic1/Topic2 LitName002 or simple relevance values.
    const STAKEHOLDER_ROLE_NONE = '__none__'; // special value for no role set
    const MULTISELECT_TYPE_IDS = new Set([14, 20, 21]); // MultiselectBox, TreeMultiselectBox, GroupedMultiselect
    // Dynamic labels will be built from actual data; this is a fallback for relevance values if role names are missing

    export default {
        name: "NetworkChart",
        components: { DxButton },
        props: {
            topicId: { type: Number, required: true },
            /**
             * edgeExpansionRules contract (explicit allow-only):
             * {
             *   level1: { edgeTltIds: number[], stakeholderRole?: { edgeTltIds: number[], allowedLitValue1: number[] } },
             *   level2: { [firstLevelTtyId: number]: number[] } // per first-level node type allowed edge type ids
             * }
             * Notes:
             * - No legacy/global arrays. If level1.edgeTltIds is empty -> no level1 edges. If level2 is missing/empty -> no second-level edges.
             */
            edgeExpansionRules: { type: Object, required: false }
        },
        setup(props, { emit }) {
            // Config refs
            const edgeConfigUri = "../Config/edgeConfig.json";
            const nodeConfigUri = "../Config/nodeConfig.json";
            const edgeConfig = ref(null);
            const nodeConfig = ref(null);

            // UI refs
            const screenContainer = ref(null);
            const sigmaContainer = ref(null);
            const popupVisible = ref(false);
            const showSecondLevel = ref(false);
            const filterProjectStakeholderProviderPrime = ref(true);
            const filteredEdgeTypes = ref([]); // kept for debug union
            // New separated filters
            const selectedEdgeTypesL1 = ref([]);
            const filteredEdgeTypesL1 = ref([]);
            const selectedEdgeTypesL2ByType = ref({}); // { [ttyId:number]: number[] }
            const filteredEdgeTypesL2ByType = ref({}); // { [ttyId:number]: EdgeTypeConfig[] }
            const l2TypeHeaders = ref([]); // [{ ttyId, ttyName }]
            const debug = ref({ enabled: false, expanded: false, stats: { level1Nodes: 0, level2Nodes: 0, finalEdges: 0 } });
            const providerPrimeApplicable = ref(false); // controls visibility of provider/prime checkbox
            const hasGraphData = ref(true);
            const loading = ref(false);
            const errorMessage = ref(null);

            // Stakeholder role filter state (for Policy-Stakeholder edges)
            const selectedStakeholderRoles = ref([]); // selected stakeholder role names (string identifiers)
            const availableStakeholderRoles = ref([]); // [{ value: string, label: string }]
            const stakeholderRoleFilterApplicable = ref(false); // controls visibility of role filter
            const stakeholderRoleFilterLabel = ref('Stakeholder Roles'); // dynamic label from config

            // Legend (nodes)
            const dynamicLayers = ref([]);
            const activeLayers = computed(() => dynamicLayers.value.filter(l => l.nodes && l.nodes.length));
            const groupedLegendItems = computed(() => {
                const groups = new Map();
                activeLayers.value.forEach(layer => {
                    let baseTypeName = layer.typeName;
                    if (baseTypeName.includes("(1st level)")) baseTypeName = baseTypeName.replace(" (1st level)", "");
                    else if (baseTypeName.includes("(2nd level)")) baseTypeName = baseTypeName.replace(" (2nd level)", "");
                    else if (baseTypeName.startsWith("Current ")) { /* keep */ }
                    if (!groups.has(baseTypeName))
                        groups.set(baseTypeName, { baseTypeName, levels: [], primaryColor: layer.color });
                    groups.get(baseTypeName).levels.push({
                        level: layer.level,
                        typeName: layer.typeName,
                        color: layer.color,
                        nodeCount: layer.nodes.length
                    });
                });
                return Array.from(groups.values()).map(g => { g.levels.sort((a, b) => a.level - b.level); return g; });
            });

            const selectedNode = ref({ TopicId: 0, ImgUrl: "", Title: "", Description: "" });

            // Data caches
            let nodes = [];
            let edges = [];
            let edgeAttributeConfig = [];
            // Fast lookup caches
            let nodesById = new Map(); // TopicId -> node
            let ttyNameById = new Map(); // TtyId -> TtyName
            // Edge config indexes
            let edgeTypeById = new Map(); // TltId -> edge type config
            let classMapByTlt = new Map(); // TltId -> Map<classValue, classCfg>

            // Graph constants (moved to module-level constants above, keep object for template bindings if needed)
            const edgeColor = { default: EDGE_COLOR_DEFAULT };

            // Sigma / graph
            let renderer = null;
            const graph = new Graph({ multi: true, type: 'mixed' });
            let drawVersion = 0; // concurrency guard
            const state = {};
            let edgeState = { type: "idle" };
            const initialSelectionSet = ref(false);
            // Layout mapping cache (avoid storing on reactive arrays)
            let typeMapping = {};

            /* ---------- Utility helpers ---------- */
            function getNodeColorByTtyId(ttyId, isCenter = false) {
                if (isCenter) return '#DC2626';
                if (!nodeConfig.value || !nodeConfig.value.nodeTypes) return '#64748B';
                const c = nodeConfig.value.nodeTypes.find(t => t.TtyId === ttyId);
                return c ? c.color : '#64748B';
            }
            function lightenColor(color, factor) {
                if (!color || !color.startsWith('#')) return color;
                const hex = color.slice(1);
                const r = parseInt(hex.substr(0, 2), 16),
                    g = parseInt(hex.substr(2, 2), 16),
                    b = parseInt(hex.substr(4, 2), 16);
                const nr = Math.min(255, Math.floor(r + (255 - r) * factor));
                const ng = Math.min(255, Math.floor(g + (255 - g) * factor));
                const nb = Math.min(255, Math.floor(b + (255 - b) * factor));
                return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
            }
            function createLayerStructure(index, ttyId, level, layerTypeName, color) {
                return { index, ttyId, level, typeName: layerTypeName, nodes: [], color };
            }
            function identifyUniqueNodeTypes(filteredNodes, centerNode) {
                const map = new Map();
                filteredNodes.forEach(n => {
                    if (n.TtyId !== centerNode.TtyId && !map.has(n.TtyId))
                        map.set(n.TtyId, { ttyId: n.TtyId, typeName: n.TtyName });
                });
                return Array.from(map.values());
            }
            function resolveArrowType(edge) {
                if (!edge) return "none";

                // Policy-Policy edges: use LitValue2 for direction
                if (edge.TltId === 6) {
                    const val = Number(edge.LitValue2);
                    switch (val) {
                        case 1: return "forward";
                        case 2: return "backward";
                        case 3: return "both";
                        default: return "none";
                    }
                }

                // All other edges: use previous logic
                if (edge.TltWithDirection) {
                    const enumVal = edge.ArrowTypeEnum != null ? edge.ArrowTypeEnum : (edge.TliArrowType >= 1 && edge.TliArrowType <= 3 ? edge.TliArrowType : null);
                    switch (enumVal) {
                        case 1: return "forward";
                        case 2: return "backward";
                        case 3: return "both";
                    }
                }
                return "none";
            }
            // Get the DisplayValue from a LinkAttributes entry by FieldName
            function getAttrDisplayValue(edge, fieldName) {
                if (!edge.LinkAttributes || !Array.isArray(edge.LinkAttributes)) return null;
                const attr = edge.LinkAttributes.find(a => a.FieldName === fieldName);
                return attr ? (attr.DisplayValue || attr.RawValue || null) : null;
            }
            function normalizeEdge(edge) {
                edge.LitName1 = edge.LitName1 || edge.LitName001 || getAttrDisplayValue(edge, 'TdgLisId001') || null;
                edge.LitName2 = edge.LitName2 || edge.LitName002 || getAttrDisplayValue(edge, 'TdgLisId002') || null;
                edge.direction = resolveArrowType(edge);
                edge.name = edge.name || edge.LitName1 || edge.LitName2 || edge.TltName || "";
            }
            function normalizeEdges() { edges.forEach(normalizeEdge); }
            function assignEdgeColors() {
                if (!edgeConfig.value) return;
                edges.forEach(edge => {
                    const cfg = edgeTypeById.get(edge.TltId);
                    if (cfg) {
                        const field = cfg.classificationField;
                        const val = edge[field];
                        const cmap = classMapByTlt.get(edge.TltId);
                        const classCfg = cmap ? cmap.get(val) : undefined;
                        if (classCfg) {
                            if (!edge.color) edge.color = classCfg.color;
                            if (!edge.width) edge.width = classCfg.width;
                            if (!edge.style) edge.style = classCfg.style;
                        } else {
                            // No matching classification: fall back to defaultColor, then first classification, then generic default
                            const fallback = cfg.defaultColor ? { color: cfg.defaultColor }
                                : (cfg.classifications && cfg.classifications.length > 0 ? cfg.classifications[0] : null);
                            if (!edge.color) edge.color = fallback?.color || edgeColor.default;
                            if (!edge.width && fallback?.width) edge.width = fallback.width;
                            if (!edge.style && fallback?.style) edge.style = fallback.style;
                        }
                    } else if (!edge.color) edge.color = edgeColor.default;
                });
            }
            function removeDuplicateEdges(raw) {
                const unique = [];
                const seen = new Set();
                raw.forEach(e => {
                    const key = `${Math.min(e.TopicId1, e.TopicId2)}-${Math.max(e.TopicId1, e.TopicId2)}-${e.TltId}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        unique.push(e);
                    }
                });
                return unique;
            }
            function showNotification(_, title, message) { alert(message, title); }
            function getSingularFromPlural(word) {
                if (word.length > 3 && word.endsWith("ies")) return word.slice(0, -3) + "y";
                if (word.match(/(ches|shes|xes|sses|zes)$/)) return word.slice(0, -2);
                if (word.endsWith("s") && !word.endsWith("ss")) return word.slice(0, -1);
                return word;
            }
            function getEdgeLabel(r) {
                let n1 = r.LitName1 || '';
                let n2 = r.LitName2 || '';

                //TODO: find a cleaner way to handle flipping n2 value for tlt 6 (policy-policy)
                if (r.TltId == 6 && n2 > ""){
                    if(r.TopicId2 === props.topicId){
                        n2 = n2.replace("1","mandated by")
                        n2 = n2.replace("2","mandating")
                    }
                    else{
                        n2 = n2.replace("1","mandating")
                        n2 = n2.replace("2","mandated by")
                    }
                    n2 = n2.replace("Both","related to")
                }

                const parts = [];
                if (n1) parts.push(n1);
                if (n2) parts.push(n2);
                return parts.join(' | ');
            }

            /* ---------- Node positioning ---------- */
            function computeCircularCoordinates(nodesArr, circleR, centerNode, level, globalTypeMapping) {
                const centerX = WIDTH / 2, centerY = HEIGHT / 2;
                if (centerNode) {
                    centerNode.x = centerX;
                    centerNode.y = centerY;
                    centerNode.size = CENTER_NODE_RADIUS * 2;
                    centerNode.radius = CENTER_NODE_RADIUS;
                    return nodesArr;
                }
                const levelRadius = level === 1 ? BASE_RADIUS_LEVEL1 : BASE_RADIUS_LEVEL2;
                if (!nodesArr.length) return nodesArr;

                const typeGroups = new Map();
                nodesArr.forEach(n => {
                    if (!typeGroups.has(n.TtyId)) typeGroups.set(n.TtyId, []);
                    typeGroups.get(n.TtyId).push(n);
                });

                const typeInfos = [];
                let cumulativeAngle = 0;
                const total = nodesArr.length;
                typeGroups.forEach((arr, typeId) => {
                    const sectorIndex = globalTypeMapping[typeId];
                    if (sectorIndex === undefined) return;
                    const ratio = arr.length / total;
                    const angle = ratio * 2 * Math.PI;
                    typeInfos.push({
                        typeId, sectorIndex, nodes: arr, nodeCount: arr.length,
                        sectorAngle: angle, startAngle: cumulativeAngle
                    });
                    cumulativeAngle += angle;
                });
                typeInfos.sort((a, b) => a.sectorIndex - b.sectorIndex);
                cumulativeAngle = 0;
                typeInfos.forEach(t => {
                    t.startAngle = cumulativeAngle;
                    t.endAngle = cumulativeAngle + t.sectorAngle;
                    cumulativeAngle += t.sectorAngle;
                });

                typeInfos.forEach(info => {
                    const { nodes: arr, startAngle, sectorAngle, nodeCount } = info;
                    const mid = startAngle + sectorAngle / 2;
                    if (nodeCount === 1) {
                        const n = arr[0];
                        n.x = centerX + levelRadius * Math.cos(mid);
                        n.y = centerY + levelRadius * Math.sin(mid);
                        n.radius = circleR;
                        n.size = circleR * 2;
                        n.level = level;
                    } else {
                        const marginFactor = Math.min(0.2, 0.8 / nodeCount);
                        const arcAngle = sectorAngle * (1 - marginFactor * 2);
                        const arcStart = startAngle + sectorAngle * marginFactor;
                        arr.forEach((n, i) => {
                            let anglePos;
                            if (nodeCount === 2) {
                                const third = sectorAngle / 3;
                                anglePos = startAngle + third + i * third;
                            } else {
                                const step = arcAngle / (nodeCount - 1);
                                anglePos = arcStart + i * step;
                            }
                            const radiusVariation = i % 2 === 0 ? 0.9 : 1.1;
                            const rn = levelRadius * radiusVariation;
                            n.x = centerX + rn * Math.cos(anglePos);
                            n.y = centerY + rn * Math.sin(anglePos);
                            n.radius = circleR;
                            n.size = circleR * 2;
                            n.level = level;
                        });
                    }
                });
                return nodesArr;
            }

            function setHoveredNode(node) {
                if (node) {
                    // When hovering a node, ensure edge-hover state is cleared
                    edgeState = { type: 'idle' };
                    state.hoveredNode = node;
                    state.hoveredNeighbors = new Set(graph.neighbors(node));
                } else {
                    state.hoveredNode = undefined;
                    state.hoveredNeighbors = undefined;
                    // Clearing node hover should also reset edge-hover conflicts
                    // (edge hover will set its own state on enterEdge)
                    // Keep edgeState unchanged here to allow smooth transition
                }
                if (renderer) renderer.refresh();
            }

            /* ---------- Data loading ---------- */
            async function loadNodeConfig() {
                try {
                    const r = await axios.get(nodeConfigUri);
                    if (r.status === 200) nodeConfig.value = r.data;
                    else showNotification("error", "Error", "Error while loading node configuration.");
                } catch (e) {
                    showNotification("error", "Error", "An error occurred while loading node configuration.");
                    console.error(e);
                }
            }
            async function loadEdgeConfig() {
                try {
                    const r = await axios.get(edgeConfigUri);
                    if (r.status === 200) {
                        edgeConfig.value = r.data;
                        buildEdgeConfigIndexes();
                    } else showNotification("error", "Error", "Error while loading edge configuration.");
                } catch (e) {
                    showNotification("error", "Error", "An error occurred while loading edge configuration.");
                    console.error(e);
                }
            }
            function buildEdgeConfigIndexes() {
                edgeTypeById = new Map();
                classMapByTlt = new Map();
                const list = edgeConfig.value?.edgeTypes || [];
                for (const t of list) {
                    edgeTypeById.set(t.TltId, t);
                    const cmap = new Map();
                    for (const c of (t.classifications || [])) {
                        cmap.set(c.value, c);
                    }
                    classMapByTlt.set(t.TltId, cmap);
                }
            }
            async function fetchData() {
                if (!props.topicId || props.topicId <= 0) return;
                loading.value = true;
                errorMessage.value = null;
                try {
                    // Ensure configs loaded before data (if not yet loaded by mount)
                    if (!edgeConfig.value || !nodeConfig.value) {
                        await Promise.all([loadEdgeConfig(), loadNodeConfig()]);
                    }
                    const [nodesResponse, edgesResponse] = await Promise.all([
                        axios.get(baseUri + "/GetSefNodes", { params: { topicId: props.topicId } }),
                        axios.get(baseUri + "/GetSefEdgesV2", { params: { topicId: props.topicId } })
                    ]);

                    if (nodesResponse.status === 200 && nodesResponse.data.success) {
                        nodes = nodesResponse.data.data || [];
                    } else {
                        throw new Error("Failed to fetch nodes");
                    }

                    if (edgesResponse.status === 200 && edgesResponse.data.success) {
                        edges = removeDuplicateEdges(edgesResponse.data.data || []);
                        normalizeEdges();
                        assignEdgeColors();
                    } else {
                        throw new Error("Failed to fetch edges");
                    }

                    // Build lookup maps after fresh data
                    nodesById = new Map(nodes.map(n => [n.TopicId, n]));
                    ttyNameById = new Map(nodes.map(n => [n.TtyId, n.TtyName]));

                    // Fetch attribute config for all topic types present in the graph
                    const uniqueTtyIds = [...new Set(nodes.map(n => n.TtyId))];
                    if (uniqueTtyIds.length > 0) {
                        try {
                            const attrConfigResponse = await axios.get(baseUri + "/GetSefEdgeAttributeConfig", {
                                params: { ttyIds: uniqueTtyIds.join(',') }
                            });
                            edgeAttributeConfig = (attrConfigResponse.status === 200 && attrConfigResponse.data.success)
                                ? (attrConfigResponse.data.data || [])
                                : [];
                        } catch (error) {
                            console.warn("Failed to fetch edge attribute config:", error);
                            edgeAttributeConfig = [];
                        }
                    } else {
                        edgeAttributeConfig = [];
                    }

                    // Set stakeholder role filter label from config (once, not per-edge)
                    const stakeholderMultiSelectCfg = edgeAttributeConfig.find(
                        cfg => cfg.TtyId === STAKEHOLDER_TTY && MULTISELECT_TYPE_IDS.has(cfg.AttributeTypeId) && cfg.Label
                    );
                    if (stakeholderMultiSelectCfg) {
                        stakeholderRoleFilterLabel.value = stakeholderMultiSelectCfg.Label;
                    }

                    drawGraph();
                } catch (error) {
                    console.error(error);
                    errorMessage.value = "Failed to load graph data.";
                    showEmpty();
                } finally {
                    loading.value = false;
                }
            }

            /* ---------- Filter Change ---------- */
            function onFilterChange() {
                // Normalize edge types to numbers; stakeholder roles are now strings (role names)
                selectedEdgeTypesL1.value = (selectedEdgeTypesL1.value || []).map(Number);
                const obj = selectedEdgeTypesL2ByType.value || {};
                Object.keys(obj).forEach(k => { obj[k] = (obj[k] || []).map(Number); });
                // Stakeholder roles are strings - trim whitespace and ensure array
                selectedStakeholderRoles.value = (selectedStakeholderRoles.value || []).map(v => typeof v === 'string' ? v.trim() : v);
                initialSelectionSet.value = true;
                rebuildGraph();
            }
            function onLevelToggle() {
                saveShowSecondLevelSetting();
                rebuildGraph();
            }
            function rebuildGraph() {
                if (loading.value) return; // avoid rebuild during loading
                if (!nodes.length || !edges.length) return; // data not yet present
                if (renderer) {
                    renderer.kill();
                    renderer = null;
                }
                graph.clear();
                drawGraph();
            }

            /* ---------- Graph Build Pipeline (Step 2 Refactor) ---------- */
            function drawGraph() {
                const centerNode = nodes.find(n => n.TopicId === props.topicId);
                if (!centerNode) { showEmpty(); return; }
                const localVersion = ++drawVersion;
                let potential = collectPotential(centerNode);
                // If auto-initialized (found new available roles while selection was empty), re-run to include edges
                if (potential.autoInitialized) {
                    potential = collectPotential(centerNode);
                }

                providerPrimeApplicable.value = potential.providerPrimeApplicable; // update visibility flag

                // If no potential edges due to empty rule configuration, abort early
                if (!potential.potentialEdgeTypeIds || potential.potentialEdgeTypeIds.size === 0) { showEmpty(); return; }
                updateEdgeTypeSelection(potential);
                const finalGraphData = computeFinalGraph(centerNode, potential);
                if (!finalGraphData || finalGraphData.filteredNodes.length <= 1 || finalGraphData.finalEdges.length === 0) {
                    showEmpty();
                    return;
                }
                if (debug.value.enabled) {
                    debug.value.stats.level1Nodes = finalGraphData.level1NodesSet.size;
                    debug.value.stats.level2Nodes = finalGraphData.secondLevelNodesSet.size;
                    debug.value.stats.finalEdges = finalGraphData.finalEdges.length;
                }
                buildLayers(centerNode, finalGraphData);
                layoutAndInstantiate(centerNode, finalGraphData);
                initRenderer(localVersion);
            }

            // Step 1: collect potential edges/nodes irrespective of current checkbox selection
            function collectPotential(centerNode) {
                const rules = props.edgeExpansionRules || null;
                const showSecond = showSecondLevel.value;
                // No rules or empty level1 edge set -> nothing to display
                if (!rules || !rules.level1 || !Array.isArray(rules.level1.edgeTltIds) || rules.level1.edgeTltIds.length === 0) {
                    return { potLevel1Nodes: new Set(), potLevel1CenterEdges: [], potInterL1Edges: [], potLevel2Nodes: new Set(), potLevel2Edges: [], potentialEdgesAll: [], potentialEdgeTypeIds: new Set(), providerPrimeApplicable: false, availablePolicyStakeholderRoles: [] };
                }
                // Build adjacency index once (nodeId -> edges[]) to reduce scans
                const adjacency = new Map();
                function addAdj(e, id) { if (!adjacency.has(id)) adjacency.set(id, []); adjacency.get(id).push(e); }
                edges.forEach(e => { addAdj(e, e.TopicId1); addAdj(e, e.TopicId2); });
                // Provider/Prime predicate (perform inline instead of O(E^2) includes)
                const isProviderPrimeEdge = (e) => {
                    const raw = e.LitValue1 ?? e.LitValue001 ?? e.LitValue01 ?? e.LitValue ?? null;
                    const num = raw !== null && raw !== undefined && raw !== '' ? Number(raw) : NaN;
                    return e.TltId === PROJECT_STAKEHOLDER_EDGE_TLT && (num === 1 || num === 2);
                };
                const isEdgeAllowed = (e) => filterProjectStakeholderProviderPrime.value ? true : !isProviderPrimeEdge(e);

                // Helper to get an attribute value from edge's LinkAttributes array
                // @param edge - the edge object with LinkAttributes array
                // @param fieldNameOrLabel - the FieldName (e.g., 'TdgMultiSelect001') or Label (e.g., 'Role') to search for
                // @returns the DisplayValue if found, or null
                const getEdgeAttribute = (edge, fieldNameOrLabel) => {
                    if (!edge.LinkAttributes || !Array.isArray(edge.LinkAttributes)) return null;
                    const attr = edge.LinkAttributes.find(a => 
                        a.FieldName === fieldNameOrLabel || 
                        a.Label === fieldNameOrLabel ||
                        (a.Label && a.Label.toLowerCase() === fieldNameOrLabel.toLowerCase())
                    );
                    return attr ? (attr.DisplayValue || attr.RawValue) : null;
                };

                // Helper to get stakeholder roles from edge
                // For Policy-Stakeholder edges, the roles are stored on the LINK itself
                // Roles are stored as slash-separated values (e.g., "Regulator/Implementer")
                // Returns an array of role strings, or empty array if none
                const getStakeholderRoles = (e) => {
                    if (e.TltId !== POLICY_STAKEHOLDER_EDGE_TLT) return [];

                    // Use edgeAttributeConfig to dynamically find multiselect-type attributes for the stakeholder type
                    const multiSelectConfigs = edgeAttributeConfig.filter(
                        cfg => cfg.TtyId === STAKEHOLDER_TTY && MULTISELECT_TYPE_IDS.has(cfg.AttributeTypeId)
                    );

                    let multiSelectValue = null;

                    if (multiSelectConfigs.length > 0) {
                        // Use config-driven lookup: try each configured multiselect field
                        for (const cfg of multiSelectConfigs) {
                            multiSelectValue = getEdgeAttribute(e, cfg.FieldName) || getEdgeAttribute(e, cfg.Label);
                            if (multiSelectValue) break;
                        }
                    } else {
                        // Fallback to hardcoded field names if config is empty
                        multiSelectValue = getEdgeAttribute(e, 'Role') || getEdgeAttribute(e, 'TdgMultiSelect001');
                    }

                    // Fallback to old flat field if LinkAttributes not present
                    if (!multiSelectValue && e.LinkMultiSelect001) {
                        multiSelectValue = e.LinkMultiSelect001;
                    }
                    // Parse slash-separated roles
                    if (multiSelectValue && multiSelectValue.trim() !== '') {
                        return multiSelectValue.split('/').map(r => r.trim()).filter(r => r !== '');
                    }
                    return [];
                };

                // Track available stakeholder roles encountered in VALID structure edges
                // Now we track by role NAME (string) instead of relevance value
                // Each edge can have multiple roles (from multiselect), so we track each individually
                const policyStakeholderRoleSet = new Set();
                const policyStakeholderRoleCount = new Map();
                const recordRole = (e) => {
                    if (e.TltId === POLICY_STAKEHOLDER_EDGE_TLT) {
                        const roles = getStakeholderRoles(e);
                        if (roles.length === 0) {
                            // No roles assigned - track as "None"
                            policyStakeholderRoleSet.add(STAKEHOLDER_ROLE_NONE);
                            policyStakeholderRoleCount.set(STAKEHOLDER_ROLE_NONE, (policyStakeholderRoleCount.get(STAKEHOLDER_ROLE_NONE) || 0) + 1);
                        } else {
                            // Track each role individually (trim as safety net)
                            for (const rawRole of roles) {
                                const role = typeof rawRole === 'string' ? rawRole.trim() : rawRole;
                                policyStakeholderRoleSet.add(role);
                                policyStakeholderRoleCount.set(role, (policyStakeholderRoleCount.get(role) || 0) + 1);
                            }
                        }
                    }
                };

                // Stakeholder role filter predicate for Policy-Stakeholder edges (TltId 10)
                // Now filters by actual role NAME (e.g., "Regulator", "Implementer") from the stakeholder topic
                // An edge is allowed if ANY of its roles are in the selected roles
                // Note: If selection is empty/null, we default to "allow all" ONLY IF we are in the auto-init phase?
                // No, sticking to "if empty show none" is safer for explicit uncheck, 
                // BUT for first load we need "Show All".
                // We handle this by checking if selection is empty but available has items -> then init and re-check.
                const isPolicyStakeholderRoleAllowed = (e) => {
                    if (e.TltId !== POLICY_STAKEHOLDER_EDGE_TLT) return true; // not a policy-stakeholder edge
                    
                    // If selection is not yet initialized (empty), treat as blocked temporarily
                    // The auto-init logic at the end will detect this and trigger a re-run with full selection.
                    if (!selectedStakeholderRoles.value || selectedStakeholderRoles.value.length === 0) return false;

                    const roles = getStakeholderRoles(e);
                    if (roles.length === 0) {
                        // No roles assigned - check if "None" is selected
                        return selectedStakeholderRoles.value.includes(STAKEHOLDER_ROLE_NONE);
                    }
                    // Edge is allowed if ANY of its roles are selected (trim defensively)
                    return roles.some(role => {
                        const trimmed = typeof role === 'string' ? role.trim() : role;
                        return selectedStakeholderRoles.value.some(sel => {
                            const selTrimmed = typeof sel === 'string' ? sel.trim() : sel;
                            return trimmed === selTrimmed;
                        });
                    });
                };

                // Level1 candidate edges: any edge directly touching center & (rule edge list empty OR edge in list)
                const level1RuleEdgeSet = new Set(rules?.level1?.edgeTltIds || []);
                const restrictLevel1 = level1RuleEdgeSet.size > 0;
                const stakeholderRoleCfg = rules?.level1?.stakeholderRole;
                const stakeholderEdgeSet = new Set(stakeholderRoleCfg?.edgeTltIds || []);
                const stakeholderAllowedLit1 = new Set(stakeholderRoleCfg?.allowedLitValue1 || []);
                const potLevel1Nodes = new Set();
                const potLevel1CenterEdges = [];

                for (const e of (adjacency.get(centerNode.TopicId) || [])) {
                    if (!isEdgeAllowed(e)) continue; // honor provider/prime filter
                    if (restrictLevel1 && !level1RuleEdgeSet.has(e.TltId)) continue;
                    // Stakeholder role predicate if applicable
                    if (stakeholderEdgeSet.size && stakeholderAllowedLit1.size) {
                        const otherId = e.TopicId1 === centerNode.TopicId ? e.TopicId2 : e.TopicId1;
                        const otherNode = nodesById.get(otherId);
                        if (otherNode?.TtyId === STAKEHOLDER_TTY) {
                            // The API is inconsistent: role value may arrive as LitValue1, LitValue001, LitValue01, LitValue, or string "1"/"2".
                        }
                    }

                    // Record role availability from structurally valid edge
                    recordRole(e);

                    // Apply stakeholder role filter
                    if (!isPolicyStakeholderRoleAllowed(e)) continue;

                    potLevel1CenterEdges.push(e);
                    const otherId = e.TopicId1 === centerNode.TopicId ? e.TopicId2 : e.TopicId1;
                    potLevel1Nodes.add(otherId);
                }
                // Inter first-level edges (collected here; will be filtered by level2 mapping later)
                let potInterL1Edges = [];
                // Scan only edges touching first-level nodes using adjacency
                for (const id of potLevel1Nodes) {
                    const lst = adjacency.get(id) || [];
                    for (const e of lst) {
                        if (!isEdgeAllowed(e)) continue;
                        if (e.TopicId1 === centerNode.TopicId || e.TopicId2 === centerNode.TopicId) continue;
                        const a = e.TopicId1, b = e.TopicId2;
                        if (potLevel1Nodes.has(a) && potLevel1Nodes.has(b)) {
                            // Record role from valid interact edge
                            recordRole(e);
                            // Apply stakeholder role filter
                            if (!isPolicyStakeholderRoleAllowed(e)) continue;
                            potInterL1Edges.push(e);
                        }
                    }
                }
                // Level2 (explicit per first-level TTY mapping):
                // Only edges where exactly one endpoint is a first-level node AND the edge type is allowed for that first-level node's TtyId
                const potLevel2Nodes = new Set();
                const potLevel2Edges = [];
                const level2Mapping = rules?.level2 && typeof rules.level2 === 'object' ? rules.level2 : null;
                // Collect potential edge type ids for L1 and L2 (for building the filter UI)
                const potentialL1EdgeTypeIds = new Set();
                const potentialL2EdgeTypeIdsByType = new Map(); // ttyId -> Set<TltId>
                if (showSecond && level2Mapping) {
                    for (const id of potLevel1Nodes) {
                        const firstLevelNode = nodesById.get(id);
                        if (!firstLevelNode) continue;
                        const allowedForTty = Array.isArray(level2Mapping[firstLevelNode.TtyId]) ? new Set(level2Mapping[firstLevelNode.TtyId].map(Number)) : new Set();
                        if (allowedForTty.size === 0) {
                            if (debug.value.enabled) console.warn('[NetworkChart] No level2 mapping for first-level TTY', firstLevelNode.TtyId, '(' + firstLevelNode.TtyName + ')');
                            continue; // no L2 from this type
                        }
                        const lst = adjacency.get(id) || [];
                        for (const e of lst) {
                            if (!isEdgeAllowed(e)) continue;
                            if (e.TopicId1 === centerNode.TopicId || e.TopicId2 === centerNode.TopicId) continue; // FIX: was 'return' causing early exit
                            const aIn = potLevel1Nodes.has(e.TopicId1);
                            const bIn = potLevel1Nodes.has(e.TopicId2);
                            if (aIn === bIn) continue; // must bridge from L1 to non-L1
                            if (!allowedForTty.has(Number(e.TltId))) continue; // type-specific restriction

                            recordRole(e);

                            // Apply stakeholder role filter
                            if (!isPolicyStakeholderRoleAllowed(e)) continue;

                            const candidateId = aIn ? e.TopicId2 : e.TopicId1;
                            potLevel2Nodes.add(candidateId);
                            potLevel2Edges.push(e);
                            // record potential L2 type for this first-level tty
                            if (!potentialL2EdgeTypeIdsByType.has(firstLevelNode.TtyId)) potentialL2EdgeTypeIdsByType.set(firstLevelNode.TtyId, new Set());
                            potentialL2EdgeTypeIdsByType.get(firstLevelNode.TtyId).add(Number(e.TltId));
                        }
                    }
                    // Re-classify inter-first-level edges as Level 2 controlled connections:
                    // Keep only those allowed by level2 mapping of either endpoint's type and expose their types for the respective headers
                    const filteredInter = [];
                    for (const e of potInterL1Edges) {
                        const nA = nodesById.get(e.TopicId1);
                        const nB = nodesById.get(e.TopicId2);
                        if (!nA || !nB) continue;
                        const allowA = Array.isArray(level2Mapping[nA.TtyId]) ? new Set(level2Mapping[nA.TtyId].map(Number)) : null;
                        const allowB = Array.isArray(level2Mapping[nB.TtyId]) ? new Set(level2Mapping[nB.TtyId].map(Number)) : null;
                        const tlt = Number(e.TltId);
                        const allowedByA = allowA ? allowA.has(tlt) : false;
                        const allowedByB = allowB ? allowB.has(tlt) : false;
                        if (allowedByA || allowedByB) {
                            filteredInter.push(e);
                            if (allowedByA) {
                                if (!potentialL2EdgeTypeIdsByType.has(nA.TtyId)) potentialL2EdgeTypeIdsByType.set(nA.TtyId, new Set());
                                potentialL2EdgeTypeIdsByType.get(nA.TtyId).add(tlt);
                            }
                            if (allowedByB) {
                                if (!potentialL2EdgeTypeIdsByType.has(nB.TtyId)) potentialL2EdgeTypeIdsByType.set(nB.TtyId, new Set());
                                potentialL2EdgeTypeIdsByType.get(nB.TtyId).add(tlt);
                            }
                        }
                    }
                    potInterL1Edges = filteredInter;
                }
                // build potential L1 edge types set (only center->L1; inter-L1 edges are controlled under Level 2 now)
                potLevel1CenterEdges.forEach(e => potentialL1EdgeTypeIds.add(Number(e.TltId)));
                const potentialEdgesAll = [...potLevel1CenterEdges, ...potInterL1Edges, ...(showSecond ? potLevel2Edges : [])];
                const potentialEdgeTypeIds = new Set(potentialEdgesAll.map(e => e.TltId));
                const providerPrimeApplicable = edges.some(e => (e.TopicId1 === centerNode.TopicId || e.TopicId2 === centerNode.TopicId) && isProviderPrimeEdge(e));

                // Build available policy-stakeholder roles list (only those present in the current graph)
                // Now using role names as values, with the name itself as the label
                const availablePolicyStakeholderRoles = Array.from(policyStakeholderRoleSet)
                    .filter(v => policyStakeholderRoleCount.get(v) > 0)
                    .sort((a, b) => {
                        if (a === STAKEHOLDER_ROLE_NONE) return 1;
                        if (b === STAKEHOLDER_ROLE_NONE) return -1;
                        // Sort alphabetically by role name
                        return String(a).localeCompare(String(b));
                    })
                    .map(v => ({ value: v, label: v === STAKEHOLDER_ROLE_NONE ? 'No role set' : v }));

                let autoInitialized = false;
                // If roles are available but selection is empty (first load), init selection and flag for re-run
                if (availablePolicyStakeholderRoles.length > 0 && (!selectedStakeholderRoles.value || selectedStakeholderRoles.value.length === 0)) {
                    if (!initialSelectionSet.value) {
                         selectedStakeholderRoles.value = availablePolicyStakeholderRoles.map(r => r.value);
                         autoInitialized = true;
                    }
                }

                return {
                    potLevel1Nodes,
                    potLevel1CenterEdges,
                    potInterL1Edges,
                    potLevel2Nodes,
                    potLevel2Edges,
                    potentialEdgesAll,
                    potentialEdgeTypeIds,
                    providerPrimeApplicable,
                    potentialL1EdgeTypeIds,
                    potentialL2EdgeTypeIdsByType,
                    availablePolicyStakeholderRoles,
                    autoInitialized
                };
            }

            // Step 2: update legend and auto-select logic
            function updateEdgeTypeSelection(potential) {
                // Build Level 1 list
                if (edgeConfig.value) {
                    filteredEdgeTypesL1.value = edgeConfig.value.edgeTypes.filter(t => potential.potentialL1EdgeTypeIds.has(t.TltId));
                } else filteredEdgeTypesL1.value = [];

                // Build Level 2 lists per first-level type and headers with names
                const l2Map = {};
                const headers = [];
                if (edgeConfig.value) {
                    potential.potentialL2EdgeTypeIdsByType.forEach((set, ttyId) => {
                        const arr = edgeConfig.value.edgeTypes.filter(t => set.has(t.TltId));
                        l2Map[ttyId] = arr;
                        // find a representative name from cached map
                        const ttyName = ttyNameById.get(Number(ttyId)) || `Type ${ttyId}`;
                        headers.push({ ttyId: Number(ttyId), ttyName });
                    });
                }
                filteredEdgeTypesL2ByType.value = l2Map;
                // Order headers by name for stable display
                headers.sort((a, b) => a.ttyName.localeCompare(b.ttyName));
                l2TypeHeaders.value = headers;

                // Maintain a flat union list for the existing debug counter
                const unionIds = new Set([...potential.potentialL1EdgeTypeIds]);
                potential.potentialL2EdgeTypeIdsByType.forEach(set => set.forEach(id => unionIds.add(id)));
                filteredEdgeTypes.value = edgeConfig.value ? edgeConfig.value.edgeTypes.filter(t => unionIds.has(t.TltId)) : [];

                // Update stakeholder role filter availability
                availableStakeholderRoles.value = potential.availablePolicyStakeholderRoles || [];
                stakeholderRoleFilterApplicable.value = availableStakeholderRoles.value.length > 0;

                // Set providerPrimeApplicable based on all edges, not just filtered ones
                providerPrimeApplicable.value = edges.some(e => e.TltId === PROJECT_STAKEHOLDER_EDGE_TLT);

                // Initialize default selections once
                if (!initialSelectionSet.value) {
                    if ((!selectedEdgeTypesL1.value || selectedEdgeTypesL1.value.length === 0) && filteredEdgeTypesL1.value.length) {
                        selectedEdgeTypesL1.value = filteredEdgeTypesL1.value.map(et => et.TltId);
                    }
                    const obj = {};
                    Object.keys(l2Map).forEach(k => {
                        const arr = l2Map[k] || [];
                        obj[k] = arr.map(et => et.TltId);
                    });
                    selectedEdgeTypesL2ByType.value = obj;
                } else {
                    // Important: If user just enabled "2nd level" and no L2 selection exists yet,
                    // initialize only L2 selections without touching L1 choices.
                    if (showSecondLevel.value) {
                        const cur = selectedEdgeTypesL2ByType.value || {};
                        const hasAnyKey = Object.keys(cur).length > 0;
                        const hasAnyL2Options = Object.keys(l2Map).length > 0;
                        if (!hasAnyKey && hasAnyL2Options) {
                            const obj = {};
                            Object.keys(l2Map).forEach(k => {
                                const arr = l2Map[k] || [];
                                obj[k] = arr.map(et => et.TltId);
                            });
                            selectedEdgeTypesL2ByType.value = obj;
                        }
                    }
                }
            }

            // Step 3: apply current selections to build final subset
            function computeFinalGraph(centerNode, potential) {
                // If both selections are empty, nothing to render
                const noL1 = !selectedEdgeTypesL1.value || selectedEdgeTypesL1.value.length === 0;
                const noL2 = !showSecondLevel.value || !selectedEdgeTypesL2ByType.value || Object.values(selectedEdgeTypesL2ByType.value).every(v => !v || v.length === 0);
                if (noL1 && noL2) return null;
                const showSecond = showSecondLevel.value;
                const selectedL1Set = new Set(selectedEdgeTypesL1.value || []);
                const level1CenterEdges = potential.potLevel1CenterEdges.filter(e => selectedL1Set.has(e.TltId));
                const level1NodesSet = new Set();
                level1CenterEdges.forEach(e => { const otherId = e.TopicId1 === centerNode.TopicId ? e.TopicId2 : e.TopicId1; level1NodesSet.add(otherId); });
                // Inter first-level edges are treated as Level 2 controlled connections now
                let interFirstLevelEdges = [];
                const secondLevelNodesSet = new Set();
                let level2Edges = [];

                if (showSecond) {
                    const selL2 = selectedEdgeTypesL2ByType.value || {};
                    // L1 -> L2 edges (classic second-level expansion)
                    level2Edges = potential.potLevel2Edges.filter(e => {
                        const aIn = level1NodesSet.has(e.TopicId1);
                        const bIn = level1NodesSet.has(e.TopicId2);
                        if (aIn === bIn) return false; // only L1->L2
                        const firstLevelId = aIn ? e.TopicId1 : e.TopicId2;
                        const firstLevelNode = nodesById.get(firstLevelId);
                        if (!firstLevelNode) return false;
                        const allowedArr = selL2[firstLevelNode.TtyId] || [];
                        if (!allowedArr.includes(e.TltId)) return false;
                        const candidateId = aIn ? e.TopicId2 : e.TopicId1;
                        secondLevelNodesSet.add(candidateId);
                        return true;
                    });
                    // Inter L1 <-> L1 edges: include only if both endpoints remain visible as L1 nodes
                    // and user L2 selection for either endpoint's type includes the edge TltId
                    interFirstLevelEdges = potential.potInterL1Edges.filter(e => {
                        if (!level1NodesSet.has(e.TopicId1) || !level1NodesSet.has(e.TopicId2)) return false;
                        const nA = nodesById.get(e.TopicId1);
                        const nB = nodesById.get(e.TopicId2);
                        const listA = selL2[nA?.TtyId] || [];
                        const listB = selL2[nB?.TtyId] || [];
                        return listA.includes(e.TltId) || listB.includes(e.TltId);
                    });
                }
                const finalEdgeList = [...level1CenterEdges, ...(showSecond ? interFirstLevelEdges : []), ...(showSecond ? level2Edges : [])];
                const seen = new Set();
                const finalEdges = [];
                for (const e of finalEdgeList) {
                    const key = `${Math.min(e.TopicId1, e.TopicId2)}-${Math.max(e.TopicId1, e.TopicId2)}-${e.TltId}`;
                    if (!seen.has(key)) { seen.add(key); finalEdges.push(e); }
                }
                const nodeLevels = new Map();
                nodeLevels.set(centerNode.TopicId, 0);
                level1NodesSet.forEach(id => nodeLevels.set(id, 1));
                secondLevelNodesSet.forEach(id => nodeLevels.set(id, 2));
                const filteredNodes = [centerNode, ...Array.from(level1NodesSet).map(id => nodesById.get(id)), ...Array.from(secondLevelNodesSet).map(id => nodesById.get(id))];
                hasGraphData.value = filteredNodes.length > 1 && finalEdges.length > 0;
                return { finalEdges, filteredNodes, nodeLevels, level1NodesSet, secondLevelNodesSet };
            }

            // Step 4: build layer metadata
            function buildLayers(centerNode, finalData) {
                dynamicLayers.value = [];
                const showSecond = showSecondLevel.value;
                const uniqueNodeTypes = identifyUniqueNodeTypes(finalData.filteredNodes, centerNode);
                const globalTypeMapping = {};
                let sectorIndex = 0;
                globalTypeMapping[centerNode.TtyId] = sectorIndex++;
                uniqueNodeTypes.forEach(t => { if (t.ttyId !== centerNode.TtyId) globalTypeMapping[t.ttyId] = sectorIndex++; });
                centerNode.x = WIDTH / 2; centerNode.y = HEIGHT / 2;
                const centerLayer = createLayerStructure(0, centerNode.TtyId, 0, 'Current ' + getSingularFromPlural(centerNode.TtyName), getNodeColorByTtyId(centerNode.TtyId, true));
                centerLayer.nodes = [centerNode];
                dynamicLayers.value.push(centerLayer);
                const maxLevel = showSecond ? 2 : 1;
                uniqueNodeTypes.forEach(nt => {
                    const baseColor = getNodeColorByTtyId(nt.ttyId);
                    for (let lvl = 1; lvl <= maxLevel; lvl++) {
                        const suffix = lvl === 1 ? '1st' : '2nd';
                        const name = `${nt.typeName} (${suffix} level)`;
                        const color = lvl === 1 ? baseColor : lightenColor(baseColor, 0.5);
                        const layer = createLayerStructure(dynamicLayers.value.length, nt.ttyId, lvl, name, color);
                        layer.nodes = finalData.filteredNodes.filter(n => n.TtyId === nt.ttyId && finalData.nodeLevels.get(n.TopicId) === lvl);
                        if (layer.nodes.length) dynamicLayers.value.push(layer);
                    }
                });
                const centerPeers = finalData.filteredNodes.filter(n => n.TtyId === centerNode.TtyId && n.TopicId !== centerNode.TopicId);
                if (centerPeers.length) {
                    const baseColor = getNodeColorByTtyId(centerNode.TtyId, false);
                    for (let lvl = 1; lvl <= (showSecond ? 2 : 1); lvl++) {
                        const peers = centerPeers.filter(n => finalData.nodeLevels.get(n.TopicId) === lvl);
                        if (!peers.length) continue;
                        const suffix = lvl === 1 ? '1st' : '2nd';
                        const layer = createLayerStructure(dynamicLayers.value.length, centerNode.TtyId, lvl, `${centerNode.TtyName} (${suffix} level)`, lvl === 1 ? baseColor : lightenColor(baseColor, 0.3));
                        layer.nodes = peers;
                        dynamicLayers.value.push(layer);
                    }
                    dynamicLayers.value.forEach((l, i) => l.index = i);
                }
                // store mapping in local cache for layout step
                typeMapping = globalTypeMapping;
            }

            // Step 5 & 6: layout nodes and instantiate graph
            function layoutAndInstantiate(centerNode, finalData) {
                graph.clear();
                const layersByLevel = new Map();
                dynamicLayers.value.forEach(l => { if (!layersByLevel.has(l.level)) layersByLevel.set(l.level, []); layersByLevel.get(l.level).push(l); });
                layersByLevel.forEach((layersInLevel, level) => {
                    const all = []; layersInLevel.forEach(l => all.push(...l.nodes));
                    if (all.length) computeCircularCoordinates(all, NODE_RADIUS, level === 0 ? centerNode : null, level, typeMapping);
                    layersInLevel.forEach(layer => {
                        layer.nodes.forEach(n => {
                            const nodeColor = (layer.level === 0 && n.TopicId === centerNode.TopicId) ? getNodeColorByTtyId(n.TtyId, true) : layer.color;
                            const zIndex = (layer.level === 0 && n.TopicId === centerNode.TopicId) ? 100 : 10;
                            graph.addNode(n.TopicId.toString(), { data: n, label: n.Title, x: n.x, y: n.y, size: n.size, color: nodeColor, zIndex });
                        });
                    });
                });
                const addedEdgeKey = new Set();
                const addVisualEdge = (sourceId, targetId, baseEdge, programType, orientedKeySuffix = '') => {
                    const l1 = finalData.nodeLevels.get(sourceId);
                    const l2 = finalData.nodeLevels.get(targetId);
                    if (l1 === 2 && l2 === 2) return; // skip 2nd-2nd
                    const key = programType === 'arrow'
                        ? `${sourceId}->${targetId}-${baseEdge.TltId}${orientedKeySuffix}`
                        : `${Math.min(sourceId, targetId)}-${Math.max(sourceId, targetId)}-${baseEdge.TltId}`;
                    if (addedEdgeKey.has(key)) return;
                    addedEdgeKey.add(key);
                    let size = baseEdge.width || EDGE_SIZE_DEFAULT;
                    if (programType === 'arrow') size = Math.max(2, size * ARROW_SIZE_MULTIPLIER); // bump size and enforce a minimum for visible arrow head
                    const color = baseEdge.color || EDGE_COLOR_DEFAULT;
                    const label = getEdgeLabel(baseEdge);
                    const attrs = { size, color, type: programType, label, zIndex: 20, style: baseEdge.style, direction: baseEdge.direction, name: baseEdge.name };
                    if (programType === 'arrow')
                        graph.addDirectedEdgeWithKey(key, sourceId.toString(), targetId.toString(), attrs);
                    else
                        graph.addUndirectedEdgeWithKey(key, sourceId.toString(), targetId.toString(), attrs);
                };

                for (const e of finalData.finalEdges) {
                    // Decide rendering program based on direction
                    const dir = e.direction || 'none';
                    if (dir === 'forward') {
                        addVisualEdge(e.TopicId1, e.TopicId2, e, 'arrow');
                    } else if (dir === 'backward') {
                        addVisualEdge(e.TopicId2, e.TopicId1, e, 'arrow');
                    } else if (dir === 'both') {
                        // Use a single edge with double-arrow program
                        addVisualEdge(e.TopicId1, e.TopicId2, e, 'double-arrow');
                    } else { // none
                        addVisualEdge(e.TopicId1, e.TopicId2, e, 'line');
                    }
                }
            }

            // Step 7: initialize renderer
            function initRenderer(localVersion) {
                if (renderer) { renderer.kill(); renderer = null; }
                nextTick(() => {
                    if (localVersion !== drawVersion) return;
                    if (!sigmaContainer.value) return;
                    try {
                        // Use default Sigma edge programs (no custom rectangle or custom arrow override)
                        renderer = new Sigma(graph, sigmaContainer.value, {
                            allowInvalidContainer: true,
                            enableEdgeHovering: true,
                            edgeHoverSizeRatio: 2,
                            edgeHoverColor: 'default',
                            defaultEdgeHoverColor: 'red',
                            edgeHoverExtremities: true,
                            enableEdgeEvents: true,
                            renderEdgeLabels: true,
                            zIndex: true,
                            // Register arrow program so edges with type 'arrow' render arrowheads
                            edgeProgramClasses: {
                                arrow: EdgeArrowProgram,
                                'double-arrow': EdgeDoubleArrowProgram,
                            },
                            labelSize: 10,
                            labelFont: 'Quicksan',
                            autoRescale: false,
                            autoResize: false,
                            skipIndexation: false
                        });
                    } catch (e) { console.error('Sigma init error:', e); return; }
                    if (!renderer) return;
                    renderer.on('enterNode', ({ node }) => setHoveredNode(node));
                    renderer.on('leaveNode', () => setHoveredNode(undefined));
                    renderer.on('clickNode', ({ node }) => { const data = graph.getNodeAttributes(node); selectedNode.value = data.data; popupVisible.value = true; });
                    renderer.on('enterEdge', ({ edge }) => {
                        // Clear any node-hover state when focusing an edge
                        setHoveredNode(undefined);
                        edgeState = { type: 'hovered', edge, source: graph.source(edge), target: graph.target(edge) };
                        renderer.refresh();
                    });
                    renderer.on('leaveEdge', () => { edgeState = { type: 'idle' }; renderer.refresh(); });
                    renderer.setSetting('nodeReducer', (node, data) => {
                        const res = { ...data };
                        // Edge hover: highlight only the two endpoint nodes, gray out others
                        if (edgeState.type === 'hovered') {
                            const isEndpoint = node === edgeState.source || node === edgeState.target;
                            if (!isEndpoint) {
                                res.label = '';
                                res.color = 'lightgray';
                                res.zIndex = 0;
                            } else {
                                // pull endpoints visually to the front
                                res.zIndex = Math.max(res.zIndex || 0, 60);
                            }
                        } else if (state.hoveredNeighbors && !state.hoveredNeighbors.has(node) && state.hoveredNode !== node) {
                            // Node hover: dim non-neighbors
                            res.label = '';
                            res.color = 'lightgray';
                            res.zIndex = 0;
                        }
                        res.labelSize = 9; return res; });
                    renderer.setSetting('edgeReducer', (edge, data) => {
                        const res = { ...data }; res.label='';
                        if (edgeState.type === 'hovered') { if (edge === edgeState.edge) { res.label=data.label; res.size=(res.size||1)*1.5; res.zIndex=50; } else { res.color='#f0f0f0'; res.zIndex=0; } } else res.color=data.color;
                        if (state.hoveredNode && !graph.hasExtremity(edge, state.hoveredNode)) res.hidden = true; res.labelSize=9; return res; });
                });
            }

            function showEmpty() {
                hasGraphData.value = false;
                if (renderer) { renderer.kill(); renderer = null; }
                graph.clear();
            }

            /* ---------- Settings persistence ---------- */
            function saveShowSecondLevelSetting() {
                sessionStorage.setItem('showSecondLevel', JSON.stringify(showSecondLevel.value));
            }
            function loadShowSecondLevelSetting() {
                const stored = sessionStorage.getItem('showSecondLevel');
                if (stored !== null) showSecondLevel.value = JSON.parse(stored);
            }

            /* ---------- UI actions ---------- */
            function refresh() { if (renderer) renderer.refresh(); }
            function onMoreInfoClick() {
                if (document.fullscreenElement) document.exitFullscreen();
                emit('openLinkedDetail', selectedNode.value.TopicId);
            }
            function toggleFullscreen() {
                if (!document.fullscreenElement) {
                    screenContainer.value.requestFullscreen()
                        .catch(err => alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`));
                } else document.exitFullscreen();
            }
            function refreshGraph() { emit('refresh'); }

            const fullscreenHandler = () => {
                if (document.fullscreenElement) screenContainer.value.classList.add('fullscreen');
                else screenContainer.value.classList.remove('fullscreen');
                if (renderer) renderer.refresh();
            };
            document.addEventListener('fullscreenchange', fullscreenHandler);

            onMounted(async () => {
                loadShowSecondLevelSetting();
                if (props.topicId > 0) {
                    await Promise.all([loadEdgeConfig(), loadNodeConfig()]);
                    fetchData();
                }
            });

            // Watchers for reactive updates
            watch(() => props.topicId, (newVal, oldVal) => {
                if (newVal && newVal !== oldVal) {
                    // reset selection to defaults for the new topic
                    initialSelectionSet.value = false;
                    selectedEdgeTypesL1.value = [];
                    selectedEdgeTypesL2ByType.value = {};
                    selectedStakeholderRoles.value = [];
                    fetchData();
                }
            });
            watch(() => props.edgeExpansionRules, () => {
                // rules changed -> reset selection derived from rules
                initialSelectionSet.value = false;
                selectedEdgeTypesL1.value = [];
                selectedEdgeTypesL2ByType.value = {};
                selectedStakeholderRoles.value = [];
                rebuildGraph();
            }, { deep: true });

            onBeforeUnmount(() => {
                document.removeEventListener('fullscreenchange', fullscreenHandler);
                if (renderer) {
                    renderer.kill();
                    renderer = null;
                }
                graph.clear();
            });

            return {
                sigmaContainer,
                screenContainer,
                popupVisible,
                refresh,
                edgeColor,
                toggleFullscreen,
                selectedNode,
                onMoreInfoClick,
                refreshGraph,
                edgeConfig,
                nodeConfig,
                onFilterChange,
                showSecondLevel,
                onLevelToggle,
                filteredEdgeTypes,
                filterProjectStakeholderProviderPrime,
                dynamicLayers,
                activeLayers,
                groupedLegendItems,
                hasGraphData,
                loading,
                errorMessage,
                providerPrimeApplicable,
                debug,
                // New L1/L2 filter bindings
                selectedEdgeTypesL1,
                filteredEdgeTypesL1,
                selectedEdgeTypesL2ByType,
                filteredEdgeTypesL2ByType,
                l2TypeHeaders,
                selectedStakeholderRoles,
                availableStakeholderRoles,
                stakeholderRoleFilterApplicable,
                stakeholderRoleFilterLabel
            };
        }
    };
</script>

<style scoped>
    .sigma-wrapper {
        position: relative;
        width: 100%;
        height: 500px;
    }

    .fullscreen .sigma-wrapper {
        height: calc(100vh - 200px);
        flex: 1;
    }

    .sigma-container {
        width: 100%;
        height: 100%;
        background-color: white;
        border-left: solid silver 1px;
        border-right: solid silver 1px;
        padding: 5px;
        box-sizing: border-box;
        overflow: hidden;
        position: relative;
    }

    .sigma-empty {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: #666;
        font-style: italic;
        border-left: solid silver 1px;
        border-right: solid silver 1px;
    }

    .fullscreen .sigma-container {
        width: 100%;
        height: 100%;
    }

    .screenContainer {
        margin: 20px auto;
        transition: all 0.3s ease;
        background-color: #ffffff;
    }

    .debug-bar {font-size:10px;border:1px solid #ddd;margin:4px 0;padding:4px;background:#fafafa;}
    .debug-header {font-weight:bold;cursor:pointer;}
    .debug-content {margin-top:4px;display:grid;grid-template-columns:auto;row-gap:2px;}

    .screenContainer.fullscreen {
        width: 100%;
        height: 100%;
        background-color: #ffffff;
        margin: 0;
        display: flex;
        flex-direction: column;
    }

    .iconRow {
        display: flex;
        justify-content: right;
        gap: 5px;
        width: 100%;
        border-top: solid silver 1px;
        border-left: solid silver 1px;
        border-right: solid silver 1px;
        border-radius: 5px 5px 0px 0px;
        padding: 2px;
        box-sizing: border-box;
    }

    .nwIcon, .refreshIcon {
        border: solid 1px gray;
        width: 20px;
        padding: 2px;
        border-radius: 5px;
        text-align: center;
        color: gray;
        cursor: pointer;
    }

    .nwIcon:hover, .refreshIcon:hover {
        color: black;
    }

    .legend-box {
        padding: 20px;
        border-left: solid silver 1px;
        border-right: solid silver 1px;
        border-bottom: solid silver 1px;
        border-radius: 0px 0px 5px 5px;
    }

    .fullscreen .legend-box {
        max-height: 30vh;
        overflow-y: auto;
    }

    .legend h3 {
        margin: 0;
        padding: 0 0 6px;
        font-size: 11px;
    }

    .legend-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        margin-bottom: 6px;
    }

    .legend-grid-extended {
        grid-template-columns: 1fr 1fr;
        overflow-y: auto;
    }

    .legend-color {
        display: inline-block;
        width: 13px;
        height: 13px;
        margin-right: 4px;
        border-radius: 10px;
    }

    .legend-group {
        margin-bottom: 8px;
        font-size: 11px;
    }

    .legend-group-header {
        display: flex;
        align-items: center;
        margin-bottom: 3px;
        font-weight: bold;
    }

    .legend-group-title {
        margin-left: 2px;
    }

    .legend-levels {
        margin-left: 17px;
    }

    .legend-level-item {
        display: flex;
        align-items: center;
        margin-bottom: 1px;
        font-size: 10px;
    }

    .legend-level-indicator {
        display: inline-block;
        width: 8px;
        height: 8px;
        margin-right: 4px;
        border-radius: 50%;
    }

    .legend-level-text {
        color: #666;
    }

    .edge-legend-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        margin-bottom: 6px;
    }

    .edge-legend-item {
        margin-bottom: 2px;
        font-size: 11px;
    }

    .edge-legend-header {
        display: flex;
        align-items: center;
    }

    input[type="checkbox"] {
        accent-color: transparent;
        margin-right: 5px;
    }

    label[for^="filter-edge-"] {
        font-size: 11px;
        padding-bottom: 2px;
    }

    .edge-classification-list {
        list-style-type: none;
        padding-left: 10px;
        margin: 0;
    }

    .edge-classification-item {
        display: flex;
        align-items: center;
        font-size: 10px;
        margin-bottom: 2px;
    }

    .edge-classification-line {
        display: inline-block;
        width: 25px;
        height: 0;
        margin-right: 4px;
    }

    .level-toggle {
        font-size: 11px;
        font-weight: bold;
    }

    .popover {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background-color: white;
        border: 1px solid #ccc;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        z-index: 1000;
        padding: 20px;
        width: 300px;
        text-align: center;
    }

    .popover-content {
        position: relative;
    }

    .close-button {
        top: 10px;
        right: 10px;
        background: none;
        border: none;
        font-size: 20px;
        cursor: pointer;
    }

    .popover-content img {
        display: block;
        margin: 0 auto 10px;
    }

    .popover-content h3 {
        margin: 10px 0;
    }

    .popover-content p {
        margin: 10px 0;
    }

    img.imgPreview {
        height: 165px;
        max-width: 295px;
        object-fit: contain;
        margin-top: 3px;
    }

    p.prevDescription {
        font-size: 11px;
    }

    .prevButtons {
        display: flex;
        justify-content: center;
    }

    .prevHeaderBar {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin: -10px -10px 5px 0;
    }

    .stakeholder-role-filter {
        margin-top: 10px;
        padding-top: 10px;
        border-top: solid 1px #ddd;
    }

    .stakeholder-role-header {
        font-weight: bold;
        margin-bottom: 6px;
        font-size: 11px;
    }

    .stakeholder-role-options {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 6px;
    }

    .stakeholder-role-item {
        display: flex;
        align-items: center;
        font-size: 10px;
        cursor: pointer;
    }

    .stakeholder-role-item input {
        margin-right: 4px;
        transform: scale(1.2);
        margin-top: 2px;
    }
</style>